import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  Alert,
  Snackbar,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  LockReset as LockResetIcon,
  Close as CloseIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  GetApp as GetAppIcon,
} from '@mui/icons-material';
import { fetchCompanyList, fetchToken, Company } from '../services/auth';
import { jwtDecode } from 'jwt-decode';
import { useUser } from '../contexts/UserContext';

interface DecodedToken {
  unique_name: string;
  UserNo: string;
  CompanyNo: string;
  MemberNo: string;
  nbf: number;
  exp: number;
  iat: number;
}

interface Notice {
  ArticleID: number;
  Title: string;
  WriteDate: string;
  Content: string;
}

const Login = () => {
  const navigate = useNavigate();
  const { fetchUserInfo, fetchCompanyInfo } = useUser();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [company, setCompany] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectOpen, setSelectOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [notices, setNotices] = useState<Notice[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstallable, setIsInstallable] = useState(false);

  const handleCompanySelectOpen = async () => {
    if (!email || !password) {
      setError('아이디와 비밀번호를 입력해주세요.');
      return;
    }

    try {
      setLoading(true);
      if (companies.length === 0) {
        const companyList = await fetchCompanyList(email, password);
        setCompanies(companyList);
        
        setError('');
        if (companyList.length === 0) {
          setError('이메일 혹은 비밀번호가 일치하지 않습니다. ');
        }
      }
      setSelectOpen(true);
    } catch (err) {
      setError('이메일 또는 비밀번호가 올바르지 않습니다.');
      setCompanies([]);
    } finally {
      
      setLoading(false);
    }
  };

  const handleCompanyChange = (event: SelectChangeEvent<string>) => {
    setCompany(event.target.value);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company) {
      setError('로그인할 업체를 선택해주세요.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // 회사 목록 조회
      const companies = await fetchCompanyList(email, password);
      const selectedCompany = companies.find(comp => comp.cust_no === company);
      
      if (!selectedCompany) {
        throw new Error('선택한 업체 정보를 찾을 수 없습니다.');
      }

      // 토큰 발급
      const response = await fetchToken(email, password, company, 'IT');
      
      if (!response.token) {
        throw new Error('로그인에 실패했습니다. 아이디와 비밀번호를 확인해주세요.');
      }
      
      // 토큰 디코딩 및 정보 저장
      try {
        const decoded = jwtDecode(response.token) as DecodedToken;
        
        // localStorage에 데이터 저장
        const saveData = () => {
          try {
            localStorage.setItem('token', response.token);
            localStorage.setItem('UserNo', decoded.UserNo);
            localStorage.setItem('CompanyNo', decoded.CompanyNo);
            localStorage.setItem('refreshToken', response.refreshToken);
            localStorage.setItem('expirationTime', response.expirationTime);
            localStorage.setItem('isLoggedIn', 'true');
            
            // 백업 저장소로 sessionStorage도 사용
            sessionStorage.setItem('token', response.token);
            sessionStorage.setItem('UserNo', decoded.UserNo);
            sessionStorage.setItem('CompanyNo', decoded.CompanyNo);
            sessionStorage.setItem('refreshToken', response.refreshToken);
            sessionStorage.setItem('expirationTime', response.expirationTime);
            sessionStorage.setItem('isLoggedIn', 'true');

            // 저장 확인
            const testRead = localStorage.getItem('token');
            if (!testRead) {
              throw new Error('Storage write failed');
            }
          } catch (storageError) {
            console.error('Storage error:', storageError);
            // 쿠키를 대체 저장소로 사용
            document.cookie = `token=${response.token}; path=/; max-age=86400`;
            document.cookie = `UserNo=${decoded.UserNo}; path=/; max-age=86400`;
            document.cookie = `CompanyNo=${decoded.CompanyNo}; path=/; max-age=86400`;
            document.cookie = `refreshToken=${response.refreshToken}; path=/; max-age=86400`;
            document.cookie = `expirationTime=${response.expirationTime}; path=/; max-age=86400`;
            document.cookie = `isLoggedIn=true; path=/; max-age=86400`;
          }
        };

        saveData();
        
        // 로그인 성공 후 유저 정보 가져오기
        await fetchUserInfo();
        await fetchCompanyInfo();
      } catch (decodeError) {
        console.error('Token decode error:', decodeError);
        setError('Invalid token format');
      }
      
      // 내일정표 페이지로 이동
      navigate('/itinerary-list', { replace: true });
    } catch (err) {
      console.error('Login failed:', err);
      setError(err instanceof Error ? err.message : '로그인에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = () => {
    setResetPasswordOpen(true);
  };

  const handleResetPasswordSubmit = async () => {
    try {
      const response = await fetch(`https://trpliveapi1.tripbox.co.kr/Users/PasswordResetEmailSend?userID=${resetEmail}`, {
        method: 'GET',
      });
      const data = await response.json();
      
      if (data === 'success') {
        setSnackbarMessage('임시비밀번호가 발급되었습니다');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setResetPasswordOpen(false);
        setResetEmail('');
      } else {
        setSnackbarMessage('비밀번호 재설정에 실패했습니다');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage('오류가 발생했습니다');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const fetchNotices = async (page: number) => {
    try {
      const response = await fetch(`https://b2capi.tripbox.co.kr/Site/GetBoardArticles?BoardID=3632&Page=${page}`);
      const data = await response.json();
      setNotices(data);
    } catch (error) {
      console.error('공지사항 조회 실패:', error);
    }
  };

  useEffect(() => {
    fetchNotices(currentPage);
  }, [currentPage]);

  const handleNoticeClick = (articleId: number) => {
    window.open(`https://info.landbox.kr/boardRead?boardID=3632&articleID=${articleId}&readable=true`, '_blank');
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    setCurrentPage(prev => prev + 1);
  };

  // PWA 설치 관련 이벤트 핸들러
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setIsInstallable(false);
      setSnackbarMessage('앱이 성공적으로 설치되었습니다!');
      setSnackbarOpen(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // 이미 설치되어 있는지 확인
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstallable(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleAppInstall = async () => {
    if (!deferredPrompt) {
      setSnackbarMessage('APP 설치가 지원되지 않는 브라우저입니다.');
      setSnackbarOpen(true);
      return;
    }

    try {
      const promptResult = await deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        setSnackbarMessage('앱 설치가 시작되었습니다.');
      } else {
        setSnackbarMessage('앱 설치가 취소되었습니다.');
      }
      setSnackbarOpen(true);
      setDeferredPrompt(null);
      setIsInstallable(false);
    } catch (error) {
      console.error('앱 설치 중 오류 발생:', error);
      setSnackbarMessage('앱 설치 중 오류가 발생했습니다.');
      setSnackbarOpen(true);
    }
  };

  return (
    <Box 
      sx={{
        display: 'flex',
        minHeight: '100vh',
        width: '100%'
      }}
    >
      {/* Left side - Login Form */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          // justifyContent: 'center',
          p: 4
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '400px',
            marginLeft: 'auto',
            marginRight: '10%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              로그인
            </Typography>
            {isInstallable && (
              <Button
                variant="outlined"
                onClick={handleAppInstall}
                startIcon={<GetAppIcon />}
                sx={{
                  ml: 2,
                  height: '36px',
                  color: 'text.primary',
                  borderColor: 'divider',
                }}>
                APP 설치
              </Button>
            )}
          </Box>
          <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
            로그인하시면 랜드박스를 이용하실수 있습니다.
          </Typography>

          <Box component="form" onSubmit={handleLogin}>
            <TextField
              margin="normal"
              required
              fullWidth
              variant="outlined"
              label="이메일 (아이디) "
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!error}
              InputLabelProps={{
                shrink: true,
                sx: {  px: 1 }
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              variant="outlined"
              name="password"
              label="비밀번호 "
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={handleCompanySelectOpen}
              error={!!error}
              InputLabelProps={{
                shrink: true,
                sx: { px: 1 }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {error && (
              <Typography color="error" sx={{ mt: 1 , fontSize: '12px' }}>
                {error}
              </Typography>
            )}

            <FormControl fullWidth margin="normal">
              <InputLabel>로그인 업체</InputLabel>
              <Select
                value={company}
                label="로그인 업체할 업체를 선택하세요"
                onSelect={handleCompanySelectOpen}
                onChange={handleCompanyChange}
                disabled={loading}
                onOpen={handleCompanySelectOpen}
                open={selectOpen}
                onClose={() => setSelectOpen(false)}
              >
                <MenuItem sx={{ color: 'text.disabled' }} value="">선택하세요</MenuItem>
                {companies.map((comp) => (
                  <MenuItem key={comp.cust_no} value={comp.cust_no}>
                    {comp.cust_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5, bgcolor: '#4B6BFB' }}
              onClick={companies.length === 0 ? handleCompanySelectOpen : handleLogin}
              disabled={loading}
            >
              {loading ? '로그인 중...' : '로그인'}
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button
                onClick={() => navigate('/register')}
                startIcon={<PersonAddIcon />}
                sx={{ color: 'text.secondary' }}
              >
                회원가입
              </Button>
              <Button
                onClick={handleResetPassword}
                startIcon={<LockResetIcon />}
                sx={{ color: 'text.secondary' }}
              >
                비밀번호 찾기
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Right side - Image */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          bgcolor: '#4B6BFB',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* 배경 이미지 */}
        <Box
          component="img"
          src="https://kr.object.iwinv.kr/orgin/1/Place/20240103130304_GettyImages-1315152008.jpg"
          alt="Welcome illustration"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.3,
            position: 'absolute'
          }}
        />
        
        {/* 콘텐츠 영역 */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            p: 4,
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 'bold', color: 'white' }}>
            Welcome! LandBox
          </Typography>
          <Typography variant="h6" sx={{ color: 'white' }}>
            Log in or create a new account.
          </Typography>

          {/* 공지사항 영역 */}
          <Box sx={{ 
            mt: 4,
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            p: 3
          }}>
            <Box sx={{ 
              mb: 2, 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Typography variant="h6" sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                color: 'white'
              }}>
                <Box component="span" sx={{ 
                  bgcolor: '#FFD700',
                  color: '#000',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.9rem',
                  fontWeight: 'bold'
                }}>
                  공지사항
                </Box>
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton 
                  size="small" 
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  sx={{ 
                    color: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    padding: '4px',
                    opacity: currentPage === 1 ? 0.5 : 1,
                    '&:hover': { 
                      bgcolor: 'rgba(255, 255, 255, 0.2)' 
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(255, 255, 255, 0.3)'
                    }
                  }}
                >
                  <NavigateBeforeIcon />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={handleNextPage}
                  disabled={notices.length < 10}
                  sx={{ 
                    color: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    padding: '4px',
                    opacity: notices.length < 10 ? 0.5 : 1,
                    '&:hover': { 
                      bgcolor: 'rgba(255, 255, 255, 0.2)' 
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(255, 255, 255, 0.3)'
                    }
                  }}
                >
                  <NavigateNextIcon />
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {notices.map((notice) => (
                <Box
                  key={notice.ArticleID}
                  onClick={() => handleNoticeClick(notice.ArticleID)}
                  sx={{
                    cursor: 'pointer',
                    p: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 1,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <Typography sx={{ 
                    color: 'white',
                    fontWeight: 'medium',
                    flex: 1,
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {notice.Title}
                  </Typography>
                  <Typography sx={{ 
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.8rem',
                    ml: 2,
                    whiteSpace: 'nowrap'
                  }}>
                    {new Date(notice.WriteDate).toLocaleDateString()}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Reset Password Dialog */}
      <Dialog
        open={resetPasswordOpen}
        onClose={() => setResetPasswordOpen(false)}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: '400px',
            borderRadius: '8px',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          비밀번호 초기화
          <IconButton
            onClick={() => setResetPasswordOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            비밀번호를 잊으셨나요?<br />
            회원 가입하신 이메일 주소를 입력해 주시면<br />
            임시 비밀번호를 이메일로 발급해 드립니다
          </Typography>
          <TextField
            required
            fullWidth
            label="아이디(이메일)"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleResetPasswordSubmit}
            sx={{
              height: '48px',
              backgroundColor: '#00A0E9',
              '&:hover': {
                backgroundColor: '#0093d5',
              },
            }}
          >
            임시 비밀번호 발급
          </Button>
        </DialogActions>
      </Dialog>
      {/* <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar> */}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{
            width: '100%',
            px: 7, py: 3, mt: 22,
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            '& .MuiAlert-icon': {
              color: 'primary.contrastText'
            },
            '& .MuiAlert-message': {
              fontSize: '18px', // Alert 메시지의 폰트 크기

            }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Login;
